import React from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import Select from "react-select";
import { Badge, Button, ButtonGroup, Card, Col, Form, Modal, Row, Spinner } from "react-bootstrap";

import PersonSearch from "../../../layout/select/person-search";
import { associate as associateFax, list as listFaxes, refresh as refreshFax } from "../../../actions/faxes";
import { directories as getDirectories, open as openFile } from "../../../actions/files";
import { isGuid } from "../../../utils/string";
import { format as formatDate } from "../../../utils/date";

const directionLabel = (direction) => direction === "inbound" ? "Received" : "Sent";

const personName = (person) => person?.displayName
    || [person?.firstName, person?.lastName].filter(Boolean).join(" ")
    || "";

const statusVariant = (status, shortStatus) => {
    const value = String(status || shortStatus || "").toLowerCase();
    if (["completed", "received", "associated", "success"].includes(value)) return "success";
    if (["failed", "failure"].includes(value)) return "danger";
    if (["pending", "submitted", "prepared"].includes(value)) return "warning";
    return "secondary";
};

class FaxWidget extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            scope: "all",
            loading: false,
            saving: false,
            items: [],
            modal: {
                show: false,
                item: null,
                personUuid: "",
                directoryUuid: null,
                directories: [],
                error: ""
            }
        };
    }

    componentDidMount = async () => {
        await this.loadData();
    };

    loadData = async () => {
        this.setState({ loading: true });
        try {
            const result = await this.props.listFaxes({
                direction: this.state.scope === "all" ? null : this.state.scope,
                size: 12
            });
            this.setState({
                loading: false,
                items: result?.success && Array.isArray(result.items) ? result.items : []
            });
        } catch {
            this.setState({
                loading: false,
                items: []
            });
        }
    };

    setScope = (scope) => {
        if (scope === this.state.scope) return;
        this.setState({ scope }, this.loadData);
    };

    openFax = async (item) => {
        if (!isGuid(item?.storedFileUuid)) return;
        await this.props.openFile({
            uuid: item.storedFileUuid,
            name: item.storedFileName || "fax"
        });
    };

    refreshItem = async (event, item) => {
        event.stopPropagation();
        if (!isGuid(item?.uuid) || item.direction === "inbound") return;
        await this.props.refreshFax({ uuid: item.uuid });
        await this.loadData();
    };

    showAssociate = async (event, item) => {
        event.stopPropagation();
        this.setState({
            modal: {
                show: true,
                item,
                personUuid: item.personUuid || "",
                directoryUuid: null,
                directories: [],
                error: ""
            }
        });
    };

    hideAssociate = () => {
        this.setState({
            modal: {
                show: false,
                item: null,
                personUuid: "",
                directoryUuid: null,
                directories: [],
                error: ""
            }
        });
    };

    setPerson = async (event) => {
        const personUuid = event?.target?.value || "";
        let directories = [];
        if (isGuid(personUuid)) {
            try {
                const result = await this.props.getDirectories({
                    ownerType: "person",
                    ownerUuid: personUuid
                });
                directories = result?.items || result?.directories || [];
            } catch {
                directories = [];
            }
        }

        this.setState((prev) => ({
            modal: {
                ...prev.modal,
                personUuid,
                directoryUuid: null,
                directories,
                error: ""
            }
        }));
    };

    associate = async () => {
        const { item, personUuid, directoryUuid } = this.state.modal;
        if (!isGuid(item?.uuid) || !isGuid(personUuid)) {
            this.setState((prev) => ({
                modal: {
                    ...prev.modal,
                    error: "Choose a person."
                }
            }));
            return;
        }

        let result = null;
        try {
            this.setState({ saving: true });
            result = await this.props.associateFax({
                uuid: item.uuid,
                personUuid,
                directoryUuid
            });
        } catch (error) {
            result = {
                success: false,
                error: {
                    message: error?.response?.data?.error?.message || error?.message
                }
            };
        } finally {
            this.setState({ saving: false });
        }

        if (!result?.success) {
            this.setState((prev) => ({
                modal: {
                    ...prev.modal,
                    error: result?.error?.message || "Unable to associate fax."
                }
            }));
            return;
        }

        this.hideAssociate();
        await this.loadData();
    };

    renderScopeButton = (scope, label) => (
        <Button
            type="button"
            variant={this.state.scope === scope ? "primary" : "outline-primary"}
            aria-pressed={this.state.scope === scope}
            onClick={() => this.setScope(scope)}
        >
            {label}
        </Button>
    );

    renderItem = (item) => {
        const inbound = item.direction === "inbound";
        const hasFile = isGuid(item.storedFileUuid);
        const associatedName = personName(item.Person);
        const title = inbound
            ? (item.fromName || item.fromFax || "Received fax")
            : (item.recipientName || item.recipientFax || "Sent fax");
        const subtitle = inbound
            ? [item.fromFax, associatedName ? `Filed to ${associatedName}` : "Unfiled"].filter(Boolean).join(" | ")
            : [item.recipientFax, item.subject].filter(Boolean).join(" | ");
        const date = item.receivedAt || item.submittedAt || item.requestedAt || item.createdAt;

        return (
            <div
                key={item.uuid}
                role="button"
                tabIndex={0}
                className="py-2 border-bottom"
                onDoubleClick={() => this.openFax(item)}
                onKeyDown={(event) => {
                    if (event.key === "Enter") this.openFax(item);
                }}
                title={hasFile ? "Double click to open" : ""}
                style={{ cursor: hasFile ? "pointer" : "default" }}
            >
                <Row className="g-2 align-items-center">
                    <Col xs="8">
                        <div className="d-flex align-items-center gap-2">
                            <Badge bg={inbound ? "info" : "primary"}>{directionLabel(item.direction)}</Badge>
                            <Badge bg={statusVariant(item.status, item.shortStatus)}>{item.status || item.shortStatus || "status"}</Badge>
                        </div>
                        <div className="fw-bold text-truncate mt-1">{title}</div>
                        <div className="small text-muted text-truncate">{subtitle}</div>
                    </Col>
                    <Col xs="4" className="text-end">
                        <div className="small text-muted">{formatDate(date, true)}</div>
                        <div className="d-flex justify-content-end gap-1 mt-1">
                            {!inbound ? (
                                <Button size="sm" variant="link" className="p-0" onClick={(event) => this.refreshItem(event, item)}>Refresh</Button>
                            ) : null}
                            {inbound && !item.personUuid && hasFile ? (
                                <Button size="sm" variant="link" className="p-0" onClick={(event) => this.showAssociate(event, item)}>File</Button>
                            ) : null}
                        </div>
                    </Col>
                </Row>
            </div>
        );
    };

    renderAssociateModal = () => {
        const { modal } = this.state;
        const directoryOptions = [
            { value: "", label: "Root" },
            ...(modal.directories || []).map((directory) => ({
                value: directory.uuid,
                label: directory.name || directory.path || directory.uuid
            }))
        ];
        const selectedDirectory = directoryOptions.find((option) => option.value === (modal.directoryUuid || "")) || directoryOptions[0];

        return (
            <Modal size="lg" backdrop="static" keyboard show={modal.show} onHide={this.hideAssociate}>
                <Modal.Header closeButton>
                    <Modal.Title>File Received Fax</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {modal.error ? <div className="alert alert-danger">{modal.error}</div> : null}
                    <Row className="g-3">
                        <Col xs="12">
                            <div className="small text-muted mb-1">Fax</div>
                            <div className="fw-semibold text-truncate">{modal.item?.storedFileName || modal.item?.fromFax || "Received fax"}</div>
                        </Col>
                        <Col xs="12">
                            <div className="top-label">
                                <PersonSearch
                                    value={modal.personUuid}
                                    name="personUuid"
                                    onChange={this.setPerson}
                                />
                                <Form.Label>PERSON</Form.Label>
                            </div>
                        </Col>
                        <Col xs="12">
                            <div className="top-label">
                                <Select
                                    value={selectedDirectory}
                                    options={directoryOptions}
                                    classNamePrefix="react-select"
                                    onChange={(option) => this.setState((prev) => ({
                                        modal: {
                                            ...prev.modal,
                                            directoryUuid: option?.value || null
                                        }
                                    }))}
                                />
                                <Form.Label>FOLDER</Form.Label>
                            </div>
                        </Col>
                    </Row>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-primary" disabled={this.state.saving} onClick={this.hideAssociate}>Cancel</Button>
                    <Button variant="primary" disabled={this.state.saving || !isGuid(modal.personUuid)} onClick={this.associate}>
                        {this.state.saving ? "Filing..." : "File Fax"}
                    </Button>
                </Modal.Footer>
            </Modal>
        );
    };

    render() {
        return (
            <>
                <Card className="h-100">
                    <Card.Header>
                        <Row className="align-items-center g-2">
                            <Col>
                                <span className="fw-bold">Faxes</span>
                            </Col>
                            <Col xs="auto">
                                <ButtonGroup size="sm" aria-label="Fax scope">
                                    {this.renderScopeButton("all", "All")}
                                    {this.renderScopeButton("inbound", "Received")}
                                    {this.renderScopeButton("outbound", "Sent")}
                                </ButtonGroup>
                            </Col>
                        </Row>
                    </Card.Header>
                    <Card.Body>
                        {this.state.loading ? (
                            <div className="text-center py-4">
                                <Spinner animation="border" />
                            </div>
                        ) : this.state.items.length ? (
                            <div>{this.state.items.map(this.renderItem)}</div>
                        ) : (
                            <div className="text-muted py-3">No faxes to show.</div>
                        )}
                    </Card.Body>
                </Card>
                {this.renderAssociateModal()}
            </>
        );
    }
}

FaxWidget.propTypes = {
    associateFax: PropTypes.func.isRequired,
    getDirectories: PropTypes.func.isRequired,
    listFaxes: PropTypes.func.isRequired,
    openFile: PropTypes.func.isRequired,
    refreshFax: PropTypes.func.isRequired
};

export default connect(null, {
    associateFax,
    getDirectories,
    listFaxes,
    openFile,
    refreshFax
})(FaxWidget);
